import React, { useState, useEffect, useCallback } from "react";
import "../styles/GalleryPage.css";

// ══════════════════════════════════════════════════════
// SECTION 1: AGM Photos
// Photos named: agm (1).JPG, agm (2).JPG ... in gallery folder
// ══════════════════════════════════════════════════════
const AGM_PHOTOS = [
  { id: 5,   src: require("../assets/photos/gallery/agm (9).jpg")   },
  { id: 12,  src: require("../assets/photos/gallery/agm (33).jpg")  },
  { id: 19,  src: require("../assets/photos/gallery/agm (104).jpg") },
  { id: 17,  src: require("../assets/photos/gallery/agm (92).jpg")  },
  { id: 1,   src: require("../assets/photos/gallery/agm (1).jpg")   },
].map(p => ({ ...p, caption: "AGM Meeting", event: "Annual General Meeting" }));

// ══════════════════════════════════════════════════════
// SECTION 2: Lagbaug Branch Opening
// Photos not available yet — will be added later
// HOW TO ADD PHOTOS WHEN READY:
// 1. Put photos in: src/assets/photos/gallery/
// 2. Name them: lagbaug (1).JPG, lagbaug (2).JPG ...
// 3. Replace LAGBAUG_PHOTOS array below with:
//    Array.from({ length: YOUR_TOTAL }, (_, i) => ({
//     id: i + 1,
//     src: require(`../assets/photos/gallery/lalbaug (${i + 1}).JPG`),
//      caption: "Lagbaug Branch Opening",
//      event: "Branch Opening",
//   }))

const LALBAUG_PHOTOS = [2,18, 5,14, 7].map((n) => ({
  id: n,
  src: require(`../assets/photos/gallery/lalbaug (${n}).jpg`),
  caption: "Lalbaug Branch Opening",
  event: "Branch Opening",
}));
// ↑ Leave empty for now. Add photos when ready as shown above.

// ── Sections config ──
const SECTIONS = [
  {
    id:       "agm",
    title:    "57th Annual General Meeting",
    subtitle: "21st August 2025 • Mumbai",
    photos:   AGM_PHOTOS,
    color:    "#27a3d7",
  },
  {
    id:       "lagbaug",
    title:    "Lagbaug Branch Opening",
    subtitle: "Branch Shifting",
    photos:   LALBAUG_PHOTOS,
    color:    "#faa727",
  },
];

export default function GalleryPage() {
  const [lbOpen,       setLbOpen]       = useState(false);
  const [lbPhotos,     setLbPhotos]     = useState([]);
  const [lbIndex,      setLbIndex]      = useState(0);
  const [lbSectionTitle, setLbSectionTitle] = useState("");

  const openLb = (photos, idx, sectionTitle) => {
    setLbPhotos(photos);
    setLbIndex(idx);
    setLbSectionTitle(sectionTitle);
    setLbOpen(true);
  };

  const closeLb   = () => setLbOpen(false);
  const prevPhoto = useCallback(() => setLbIndex(i => (i - 1 + lbPhotos.length) % lbPhotos.length), [lbPhotos]);
  const nextPhoto = useCallback(() => setLbIndex(i => (i + 1) % lbPhotos.length), [lbPhotos]);

  const handleKey = useCallback((e) => {
    if (!lbOpen) return;
    if (e.key === "ArrowLeft")  prevPhoto();
    if (e.key === "ArrowRight") nextPhoto();
    if (e.key === "Escape")     closeLb();
  }, [lbOpen, prevPhoto, nextPhoto]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  useEffect(() => {
    document.body.style.overflow = lbOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lbOpen]);

  return (
    <div className="gallerypage">

      {/* ── HERO ── */}
      <section className="gallerypage-hero">
        <h1 className="gallerypage-hero-title">Photo Gallery</h1>
        <p className="gallerypage-hero-sub">
          Celebrating milestones, events and community moments of The Sahyadri Sahakari Bank Ltd
        </p>
      </section>

      {/* ── SECTIONS ── */}
      {SECTIONS.map((section) => (
        <section key={section.id} className="gallerypage-section">

          {/* Section header */}
          <div className="gallerypage-section-head">
            <div
              className="gallerypage-section-accent"
              style={{ background: section.color }}
            />
            <div>
              <h2 className="gallerypage-section-title">{section.title}</h2>
              <p className="gallerypage-section-sub">{section.subtitle}</p>
            </div>
            {section.photos.length > 0 && (
              <span className="gallerypage-section-count">
                {section.photos.length} Photos
              </span>
            )}
          </div>

          {/* Photos or coming soon */}
          {section.photos.length > 0 ? (
            <div className="gallerypage-grid">
              {section.photos.map((photo, idx) => (
                <div
                  key={photo.id}
                  className="gallerypage-photo"
                  onClick={() => openLb(section.photos, idx, section.title)}
                >
                  <img
                    src={photo.src}
                    alt={photo.caption}
                    className="gallerypage-photo-img"
                  />
                  <div className="gallerypage-photo-hover">
                    <div className="gallerypage-hover-icon">+</div>
                    <div className="gallerypage-hover-txt">View</div>
                  </div>
                  <div className="gallerypage-caption-bar">
                    <span>{photo.caption}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="gallerypage-coming-soon">
              <div
                className="gallerypage-cs-icon"
                style={{ borderColor: section.color, color: section.color }}
              >
                📷
              </div>
              <p className="gallerypage-cs-text">Photos coming soon</p>
              <p className="gallerypage-cs-sub">
                Photos from this event will be added shortly. Please check back later.
              </p>
            </div>
          )}

        </section>
      ))}

      {/* ── LIGHTBOX ── */}
      {lbOpen && lbPhotos.length > 0 && (
        <div
          className="gallerypage-lb"
          onClick={(e) => { if (e.target.classList.contains("gallerypage-lb")) closeLb(); }}
        >
          <button className="gallerypage-lb-close" onClick={closeLb}>×</button>

          <div className="gallerypage-lb-imgwrap">
            <img
              src={lbPhotos[lbIndex].src}
              alt={lbPhotos[lbIndex].caption}
              className="gallerypage-lb-img"
            />
          </div>

          <div className="gallerypage-lb-info">
            <div className="gallerypage-lb-caption">{lbSectionTitle}</div>
            <div className="gallerypage-lb-event">{lbPhotos[lbIndex].event}</div>
          </div>

          <div className="gallerypage-lb-nav">
            <button className="gallerypage-lb-btn" onClick={prevPhoto}>&#8592; Prev</button>
            <button className="gallerypage-lb-btn gallerypage-lb-btn--close" onClick={closeLb}>Close</button>
            <button className="gallerypage-lb-btn" onClick={nextPhoto}>Next &#8594;</button>
          </div>

          <div className="gallerypage-lb-counter">
            {lbIndex + 1} / {lbPhotos.length}
          </div>
        </div>
      )}

    </div>
  );
}